import React from 'react';
/**
 * Custom hook to detect overflow in a DOM element.
 *
 * Attaches a `ResizeObserver` to a DOM node and updates the state when
 * the content overflows either horizontally or vertically.
 *
 * @returns {[React.RefObject<HTMLElement>, boolean]} - A tuple:
 * 1. `ref`: A React ref to be attached to the target element.
 * 2. `isOverflowing`: A boolean indicating whether the element's content is overflowing.
 */
export function useOverflowDetect() {
    const ref = React.useRef(null);
    const [isOverflowing, setOverflowing] = React.useState(false);
    React.useEffect(() => {
        const element = ref.current;
        if (!element) {
            return;
        }
        const resizeObserver = new ResizeObserver(() => {
            setOverflowing(element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth);
        });
        resizeObserver.observe(element);
        return () => resizeObserver.disconnect();
    }, [ref]);
    return [ref, isOverflowing];
}
